<script setup lang="ts">
import MessageBackgroundBlur from "@/components/common/OptionsSettings/MessageBackgroundBlur.vue";
import MarginPaddingInput from "@/components/generic/MarginPaddingInput.vue";
import { useAppStore } from "@/store/app";
import { computeDominantColorFromImage } from "@/util/color";
import { UICollapse, UICollapseItem, UISelect, UITextXsMedium } from "@gohighlevel/ghl-ui";
import { computed, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import ColorInput from "../Elements/ColorInput.vue";
import PixelNumber from "../Elements/PixelNumber.vue";

const { t } = useI18n();
const store = useAppStore();
const isMobile = computed(() => (store as any).deviceMode === "mobile");

// Font weight options from 100 to 700
const fontWeightOptions = [
  { label: "100 - Thin", value: 100 },
  { label: "200 - Extra Light", value: 200 },
  { label: "300 - Light", value: 300 },
  { label: "400 - Normal", value: 400 },
  { label: "500 - Medium", value: 500 },
  { label: "600 - Semi Bold", value: 600 },
  { label: "700 - Bold", value: 700 },
];

const formStoreSubmitMessageStyle = store.getFormSubmitMessageStyle;
const formSubmitMessageStyle = reactive(formStoreSubmitMessageStyle);

const handleThankYouMessageBackground = (val: string) => {
  // Lock auto color updates when user picks a custom color; blur toggle remains untouched
  (store.submitMessageStyle as any).autoBgFromImageColorLocked = true;
  if (isMobile.value) {
    (store.submitMessageStyle as any).mobileBgColor = val;
  } else {
    store.submitMessageStyle.bgColor = val;
  }
  store.anyUnsavedChanges = true;
};

const handleLabelFontWeight = (val: number) => {
  if (isMobile.value) {
    (store.submitMessageStyle as any).mobileFontWeight = val;
  } else {
    store.submitMessageStyle.fontWeight = val;
  }
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const onUpdateSpace = (type: string, side: string, value: number | string) => {
  if (!(store.submitMessageStyle as any).padding) {
    (store.submitMessageStyle as any).padding = { top: 48, right: 48, bottom: 48, left: 48 };
  }
  if (!(store.submitMessageStyle as any).margin) {
    (store.submitMessageStyle as any).margin = {
      top: "auto",
      right: "auto",
      bottom: "auto",
      left: "auto",
    };
  }
  const target = isMobile.value
    ? type === "padding"
      ? (store.submitMessageStyle as any).mobilePadding ||
        ((store.submitMessageStyle as any).mobilePadding = {
          top: 48,
          right: 48,
          bottom: 48,
          left: 48,
        })
      : (store.submitMessageStyle as any).mobileMargin ||
        ((store.submitMessageStyle as any).mobileMargin = {
          top: "auto",
          right: "auto",
          bottom: "auto",
          left: "auto",
        })
    : type === "padding"
    ? (store.submitMessageStyle as any).padding
    : (store.submitMessageStyle as any).margin;
  if (type === "padding") {
    (target as any)[side] = value === "auto" ? 0 : Number(value) || 0;
  } else if (type === "margin") {
    const prev = (target as any)[side];
    if (
      (value === 0 || value === "0" || value === undefined || value === null) &&
      prev === "auto"
    ) {
      (target as any)[side] = "auto";
    } else {
      (target as any)[side] = value;
    }
  }
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

// Helper to get the active background image URL based on device mode
const getCurrentBgUrl = (): string => {
  const mode = String((store as any).deviceMode || "desktop");
  const url =
    mode === "mobile"
      ? store.formStyle?.mobileBgImage || store.formStyle?.bgImage || ""
      : store.formStyle?.bgImage || "";
  return (url || "").trim();
};

// Helper function to recompute background color from current background image
const recomputeBackgroundColor = async () => {
  const isColorLocked = Boolean((store.submitMessageStyle as any).autoBgFromImageColorLocked);
  if (isColorLocked) {
    return;
  }
  const useAuto = Boolean((store.submitMessageStyle as any).autoBgFromImage);
  if (!useAuto) {
    return;
  }
  const bgUrl = getCurrentBgUrl();
  if (!bgUrl) {
    // No-op if no bg image as per requirement
    return;
  }
  try {
    const color = await computeDominantColorFromImage(bgUrl);
    const newColor = color.replace("#", "");

    // Get current color based on device mode
    const currentColor = isMobile.value
      ? (store.submitMessageStyle as any).mobileBgColor ?? store.submitMessageStyle.bgColor
      : store.submitMessageStyle.bgColor;

    // Only update and mark as changed if color actually changed
    if (newColor === currentColor) {
      return;
    }

    // Persist hex only (alpha handled in preview)
    if (isMobile.value) {
      (store.submitMessageStyle as any).mobileBgColor = newColor;
    } else {
      store.submitMessageStyle.bgColor = newColor;
    }
  } catch (error) {
    console.error("Error computing dominant color from image:", error);
  }
};

// Recompute on bg image changes (desktop or mobile), and run immediately on mount
watch(
  () => [store.formStyle?.bgImage, store.formStyle?.mobileBgImage],
  async () => {
    const url = getCurrentBgUrl();
    if (url) {
      await recomputeBackgroundColor();
    }
  },
  { immediate: true }
);
watch(
  () => (store.submitMessageStyle as any).autoBgFromImage,
  async (enabled) => {
    if (!enabled) {
      return;
    }
    const url = getCurrentBgUrl();
    if (url) {
      await recomputeBackgroundColor();
    }
  }
);

// Device-aware getters for UI
const bgColorForDevice = computed(
  () =>
    (isMobile.value
      ? (store.submitMessageStyle as any).mobileBgColor ?? store.submitMessageStyle.bgColor
      : store.submitMessageStyle.bgColor) as string
);
const fontWeightForDevice = computed(
  () =>
    (isMobile.value
      ? (store.submitMessageStyle as any).mobileFontWeight ?? store.submitMessageStyle.fontWeight
      : store.submitMessageStyle.fontWeight) as number
);
const spaceForDevice = computed(() => {
  return {
    padding: (isMobile.value
      ? (store.submitMessageStyle as any).mobilePadding ?? store.submitMessageStyle.padding
      : store.submitMessageStyle.padding) as any,
    margin: (isMobile.value
      ? (store.submitMessageStyle as any).mobileMargin ?? store.submitMessageStyle.margin
      : store.submitMessageStyle.margin) as any,
  };
});

const cornerRadius = computed(() => Number((store.submitMessageStyle as any).cornerRadius ?? 10));

// Recompute when device mode changes, run immediately as well
watch(
  () => (store as any).deviceMode,
  async () => {
    const url = getCurrentBgUrl();
    if (url) {
      await recomputeBackgroundColor();
    }
  },
  { immediate: true }
);
</script>

<template>
  <section class="message-styling flex flex-col items-stretch justify-center self-stretch p-3">
    <UICollapse
      id="message-styling-collapse"
      :arrow-placement="'right'"
      :default-expanded-names="['message-styling']"
    >
      <UICollapseItem
        id="message-styling"
        :name="'message-styling'"
        :title="t('common.messageStyling')"
      >
        <template #header>
          <div class="flex w-full items-center">
            <span class="text-sm font-medium text-gray-900">
              {{ t("common.messageStyling") }}
            </span>
          </div>
        </template>

        <section class="form_settings">
          <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
            <ColorInput
              :label="t('common.background')"
              :compact="true"
              :color="bgColorForDevice"
              @update:color="handleThankYouMessageBackground"
            />
            <div class="flex flex-col items-start gap-2 self-stretch">
              <UITextXsMedium>{{ t("common.weight") }}</UITextXsMedium>
              <UISelect
                id="font-weight-select"
                :value="fontWeightForDevice"
                :options="fontWeightOptions"
                size="large"
                @update:value="handleLabelFontWeight"
              />
            </div>
            <PixelNumber
              :label="t('common.cornerRadius')"
              :value="cornerRadius"
              :min="0"
              :max="100"
              @update:value="(value) => ((store.submitMessageStyle as any).cornerRadius = value)"
            />
          </div>

          <MarginPaddingInput
            class="mt-2 w-full"
            :space="spaceForDevice"
            :padding-units-config="['px']"
            :margin-units-config="['auto']"
            @updateSpace="onUpdateSpace"
          />

          <div class="border-t border-gray-200">
            <MessageBackgroundBlur />
          </div>
        </section>
      </UICollapseItem>
    </UICollapse>
  </section>
</template>

<style>
.message-styling {
  border-radius: 12px;
  border: 1px solid #eaecf0;
  background: #ffffff;
}

.message-styling .n-collapse-item__header-main {
  text-transform: none !important;
}
</style>
